import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useEffect, useState } from "react";
import NoData from "../../../assets/images/noData.svg";
import { theme } from "../../../utils/styles";
import customTableStyles from "./customTable.styles";
import usePagination from "./Pagination";
import paginationStyles from "./Pagination.styles";

interface CustomProps {
  headers: any[];
  rows: any[];
  customClasses?: { headerCellClass?: string; rowClass?: string };
  size?: any;
  searchFields?: string[];
  handleRowClick?: Function;
  handlePageChange?: Function;
  handleRowsPerPage?: Function;
  paginationCount?: number;
  rowsPerPage?: number;
  pageNumber?: number;
  currentSelectedTab?: string;
  checkboxSelection?: boolean;
  handleClick?: any;
  onSelectAllClick?: any;
  isSelected?: any;
  isSelectAll?: any;
  isLoading?: boolean;
  searchParam?: string;
  setPage?: any;
  handlePerPageData?: any;
  perPageData?: any;
  isRowPerPageEnable?: boolean;
  isPagination?: boolean;
  deleteIcon?: boolean;
}

const CustomTable = (props: CustomProps) => {
  const classes = customTableStyles;
  const pagination = paginationStyles;
  // const [page, setPage] = useState<number>(1);
  const [rowData, setRowData] = useState(props.rows);
  const [selected, setSelected] = useState<any>();
  const rowPerPageData = [10, 15, 20, 25];
  useEffect(() => {
    setSelected(props?.rows?.map((item: any) => item?.id));
    setRowData(props?.rows);
  }, [props.rows]);

  const getRowOnClickHandler = (row: any) => {
    let handleRowClick = props.handleRowClick
      ? {
          onClick: () => props.handleRowClick && props.handleRowClick(row),
        }
      : {};
    return handleRowClick;
  };

  const PER_PAGE = props.rowsPerPage ?? 10;
  const dataCount = Math.ceil(props.paginationCount! / PER_PAGE);
  const finalTableData = usePagination(rowData, PER_PAGE);

  const getRowData = (row: any) => {
    return props?.headers?.map((column, index) => (
      <>
        <TableCell sx={classes.tableCell} align={column["align"]} key={index}>
          {row[column["field"]]}
        </TableCell>
      </>
    ));
  };

  const checkboxStyles = {
    color: theme.palette.primary.contrastText,
    "&.Mui-checked": {
      color: theme.palette.primary.contrastText,
    },
  };

  const getHeaders = () => {
    let checkValue: boolean | null = null;
    if (props?.isSelectAll?.length)
      checkValue = selected?.every((item: any) =>
        props.isSelectAll?.includes(item)
      );
    return (
      <TableHead>
        <TableRow style={{ borderRadius: "10px" }}>
          {props.checkboxSelection && (
            <TableCell
              padding="checkbox"
              sx={{
                ...classes.tableHeaderCell,
                borderBottom: "none",
              }}
            >
              <Checkbox
                sx={checkboxStyles}
                checked={checkValue ?? false}
                onChange={props.onSelectAllClick}
                inputProps={{
                  "aria-label": "select all desserts",
                }}
              />
            </TableCell>
          )}
          {props.headers.map((column, index) => (
            <TableCell
              sx={classes.tableHeaderCell}
              align={column["align"]}
              key={index}
            >
              {column.name}
            </TableCell>
          ))}
          {props.deleteIcon && (
            <TableCell sx={classes.tableHeaderCell}>Delete</TableCell>
          )}
        </TableRow>
      </TableHead>
    );
  };

  const getRows = () => {
    return (
      <TableBody sx={classes.tableBody}>
        {finalTableData.currentData()?.map((row: any, index: any) => {
          return (
            <>
              <TableRow
                sx={
                  props.handleClick
                    ? {
                        ...classes.tableRow,
                        "&:hover": {
                          cursor: "pointer",
                        },
                      }
                    : classes.tableRow
                }
                key={index}
                {...getRowOnClickHandler(row)}
              >
                {props.checkboxSelection && (
                  <TableCell padding="checkbox" sx={{ borderBottom: "none" }}>
                    <Checkbox
                      onClick={(event) =>
                        props.handleClick ? props.handleClick(event, row) : null
                      }
                      sx={checkboxStyles}
                      checked={
                        props.isSelected ? props.isSelected(row.id) : null
                      }
                      inputProps={{
                        "aria-labelledby": `enhanced-table-checkbox-${index}`,
                      }}
                    />
                  </TableCell>
                )}
                {getRowData(row)}
                {props.deleteIcon && (
                  <TableCell sx={classes.tableCell}>
                    <DeleteIcon
                      style={{ cursor: "pointer" }}
                      onClick={(event) =>
                        props.handleClick ? props.handleClick(event, row) : null
                      }
                    />
                  </TableCell>
                )}
              </TableRow>
            </>
          );
        })}
      </TableBody>
    );
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    props.handlePageChange && props.handlePageChange(event, newPage - 1);
    props.setPage && props.setPage(newPage);
  };
  const handleChangePerPageData = (event: any) => {
    props.handlePerPageData && props.handlePerPageData(event);
  };
  const getTablePagination = () => {
    return (
      <>
        <Box
          style={{
            display: "flex",
            justifyContent: props.isRowPerPageEnable ? "end" : "space-between",
            flexWrap: "wrap",
          }}
        >
          {!props.isRowPerPageEnable && (
            <>
              <Box
                style={{
                  display: "flex",
                  marginTop: "8px",
                  alignItems: "center",
                }}
              >
                <InputLabel
                  sx={{
                    color: theme.palette.primary.contrastText,
                    opacity: 0.7,
                  }}
                >
                  Row per page:
                </InputLabel>
                <Select
                  value={props.perPageData}
                  onChange={handleChangePerPageData}
                  id="rowPerPage"
                  name="rowPerPage"
                  sx={{
                    height: "32px",
                    marginLeft: "8px",
                    backgroundColor: "rgba(255, 255, 255, 0.14)",
                    backdropFilter: "blur(24px)",
                    color: theme.palette.primary.contrastText,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-icon": {
                      color: theme.palette.primary.contrastText,
                    },
                    "&:hover": {
                      "&& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                >
                  {rowPerPageData.map((items) => (
                    <MenuItem
                      value={items}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.primary.light,
                        },
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      {items}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </>
          )}
          <Box>
            <Pagination
              page={props.pageNumber!}
              count={dataCount}
              shape="rounded"
              size="medium"
              sx={pagination.pageBtn}
              onChange={(event: any, page: any) => {
                handleChangePage(event, page);
              }}
              showLastButton
              showFirstButton
            />
          </Box>
        </Box>
      </>
    );
  };
  const getTable = () => {
    return (
      <Box>
        <Table sx={classes.table}>
          {getHeaders()}
          {!props.isLoading && rowData?.length <= 0 ? (
            <TableCell
              colSpan={7}
              style={{
                border: "none",
                background: "rgba(255, 255, 255, 0.14)",
                // backdropFilter: "blur(24px)",
                boxShadow: "inset 0 500px rgba(255, 255, 255, .08)",
                borderRadius: "10px",
              }}
            >
              <Box textAlign="center">
                <Box
                  component="img"
                  src={NoData}
                  overflow="auto"
                  height="150px"
                  width="100%"
                />
                <Typography sx={classes.mediumFonts}>
                  No Data Available!
                </Typography>
              </Box>
            </TableCell>
          ) : (
            getRows()
          )}
        </Table>
        {props.paginationCount && rowData?.length ? getTablePagination() : null}
      </Box>
    );
  };
  return getTable();
};

export default CustomTable;
